import { Fragment, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Download, RotateCcw, Save, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { RbacOperationsPanel } from '@/pages/admin/RbacOperationsPanel';
import { RbacGridPanel } from '@/pages/admin/RbacGridPanel';
import { humanRole } from '@/lib/humanise';
import type { PageKey } from '@/lib/permissions';
import type { UserRole } from '@/contexts/AuthContext';
import {
  ALL_MODULES,
  ALL_ROLES,
  CRUD_OPS,
  LEVEL_HINTS,
  LEVEL_LABELS,
  LEVEL_PRESETS,
  RBAC_MODULE_GROUPS,
  RBAC_ROLE_GROUPS,
  UNENFORCED_ROLES,
  clearDraft,
  countDrift,
  exportMatrix,
  isDrifted,
  levelOf,
  liveLevelFor,
  loadDraft,
  normalisePerms,
  permsFor,
  saveDraft,
  seedMatrix,
  type AccessLevel,
  type CrudOp,
  type DisplayLevel,
  type RbacMatrix,
} from '@/lib/rbacModel';

/**
 * RBAC configuration — /admin/rbac, Platform Admin only.
 *
 * Two views over one model:
 *   Configure  role-centric. Per module: the four standard levels as presets,
 *              and the underlying Create / Read / Update / Delete flags.
 *   Matrix     the client's Role x Module grid, one letter per cell.
 *
 * The "Live" column is recomputed from the app's real permission tables on
 * every render, never from the draft, so intent and reality cannot silently
 * diverge. See lib/rbacModel.ts for why this configures rather than enforces.
 */

const LEVEL_ORDER: readonly AccessLevel[] = ['N', 'V', 'C', 'A'];

const LEVEL_CELL: Record<DisplayLevel, string> = {
  N: 'bg-slate-100 text-slate-400 border-slate-200',
  V: 'bg-sky-50 text-sky-700 border-sky-200',
  C: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  A: 'bg-amber-50 text-amber-800 border-amber-300',
  CUSTOM: 'bg-violet-50 text-violet-700 border-violet-300',
};

const LEVEL_SHORT: Record<DisplayLevel, string> = {
  N: 'N',
  V: 'V',
  C: 'C',
  A: 'A',
  CUSTOM: '*',
};

export default function RbacConfigurationPage() {
  const [matrix, setMatrix] = useState<RbacMatrix>(() => loadDraft() ?? seedMatrix());
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<'grid' | 'operations' | 'configure' | 'matrix'>('grid');
  const [selectedRole, setSelectedRole] = useState<UserRole>('site_admin');

  const driftTotal = useMemo(() => countDrift(matrix), [matrix]);

  const driftByRole = useMemo(() => {
    const out: Record<string, number> = {};
    for (const role of ALL_ROLES) {
      out[role] = ALL_MODULES.reduce(
        (n, m) => n + (isDrifted(matrix, role, m.key) ? 1 : 0),
        0,
      );
    }
    return out;
  }, [matrix]);

  const setPerms = useCallback(
    (role: UserRole, key: PageKey, next: Parameters<typeof normalisePerms>[0]) => {
      setMatrix((prev) => ({
        ...prev,
        [role]: { ...prev[role], [key]: normalisePerms(next) },
      }));
      setDirty(true);
    },
    [],
  );

  const applyLevel = useCallback(
    (role: UserRole, key: PageKey, level: AccessLevel) => {
      setPerms(role, key, { ...LEVEL_PRESETS[level] });
    },
    [setPerms],
  );

  const applyLevelToRole = useCallback(
    (role: UserRole, level: AccessLevel) => {
      setMatrix((prev) => ({
        ...prev,
        [role]: Object.fromEntries(
          ALL_MODULES.map((m) => [m.key, { ...LEVEL_PRESETS[level] }]),
        ),
      }));
      setDirty(true);
      toast.success(`${humanRole(role)} set to "${LEVEL_LABELS[level]}" on every module.`);
    },
    [],
  );

  const toggleOp = useCallback(
    (role: UserRole, key: PageKey, op: CrudOp) => {
      const current = permsFor(matrix, role, key);
      setPerms(role, key, { ...current, [op]: !current[op] });
    },
    [matrix, setPerms],
  );

  const onSave = () => {
    if (saveDraft(matrix)) {
      setDirty(false);
      toast.success('Draft saved in this browser.');
    } else {
      toast.error('Could not save — browser storage is unavailable.');
    }
  };

  const onReset = () => {
    setMatrix(seedMatrix());
    clearDraft();
    setDirty(false);
    toast.success('Reset to the permissions the app enforces today.');
  };

  const onExport = async () => {
    try {
      await navigator.clipboard.writeText(exportMatrix(matrix));
      toast.success('Configuration JSON copied to the clipboard.');
    } catch {
      toast.error('Could not copy — check clipboard permissions.');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="RBAC configuration"
        subtitle="Per role, per module, at Create / Read / Update / Delete granularity. Seeded from the permissions the app enforces today."
        actions={
          // These act on the advisory page-grid draft only. The Operations tab
          // has its own Save and apply, and showing both would invite someone
          // to press "Save draft" expecting it to take effect.
          tab === 'operations' || tab === 'grid' ? null : (
            <div className="flex items-center gap-2">
              <button type="button" onClick={onExport} className="btn-secondary">
                <Download className="h-3.5 w-3.5" />
                Export JSON
              </button>
              <button type="button" onClick={onReset} className="btn-secondary">
                <RotateCcw className="h-3.5 w-3.5" />
                Reset to live
              </button>
              <button type="button" onClick={onSave} className="btn-primary" disabled={!dirty}>
                <Save className="h-3.5 w-3.5" />
                {dirty ? 'Save draft' : 'Saved'}
              </button>
            </div>
          )
        }
      />

      {/* The advisory warning belongs to the page-level tabs only — the
          Operations tab carries its own banner saying the opposite. */}
      {tab !== 'operations' && tab !== 'grid' && <NotEnforcedBanner driftTotal={driftTotal} />}

      <div className="flex items-center gap-1 border-b border-surface-border">
        <TabButton active={tab === 'grid'} onClick={() => setTab('grid')}>
          Permissions · enforced
        </TabButton>
        <TabButton active={tab === 'operations'} onClick={() => setTab('operations')}>
          Operations · detail
        </TabButton>
        <TabButton active={tab === 'configure'} onClick={() => setTab('configure')}>
          Pages · advisory
        </TabButton>
        <TabButton active={tab === 'matrix'} onClick={() => setTab('matrix')}>
          Matrix overview
        </TabButton>
      </div>

      {tab === 'grid' ? (
        <RbacGridPanel />
      ) : tab === 'operations' ? (
        <RbacOperationsPanel />
      ) : tab === 'configure' ? (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-6">
          <RoleList
            selected={selectedRole}
            onSelect={setSelectedRole}
            driftByRole={driftByRole}
          />
          <RoleEditor
            role={selectedRole}
            matrix={matrix}
            onLevel={applyLevel}
            onToggleOp={toggleOp}
            onBulkLevel={applyLevelToRole}
          />
        </div>
      ) : (
        <MatrixOverview matrix={matrix} onJumpToRole={(r) => { setSelectedRole(r); setTab('configure'); }} />
      )}

      {/* N/V/C/A describes the page grid, not the operation registry. */}
      {tab !== 'operations' && tab !== 'grid' && <Legend />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function NotEnforcedBanner({ driftTotal }: { driftTotal: number }) {
  return (
    <div className="flex items-start gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3">
      <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-900">
        <p className="font-semibold">This configuration is not enforced yet.</p>
        <p className="mt-0.5 text-amber-800">
          Access is still decided by the permission tables in the code. This screen records the
          intended grid and exports it; wiring it to the guards is a separate piece of work.
          {driftTotal > 0 && (
            <>
              {' '}
              <span className="font-semibold">
                {driftTotal} cell{driftTotal === 1 ? '' : 's'}
              </span>{' '}
              currently differ from what the app enforces.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-navy-600 text-ink'
          : 'border-transparent text-ink-muted hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function RoleList({
  selected,
  onSelect,
  driftByRole,
}: {
  selected: UserRole;
  onSelect: (r: UserRole) => void;
  driftByRole: Record<string, number>;
}) {
  return (
    <nav className="rounded border border-surface-border bg-white overflow-hidden self-start">
      {RBAC_ROLE_GROUPS.map((group) => (
        <div key={group.label}>
          <div className="px-3 py-2 bg-surface-muted/40 border-b border-surface-border">
            <p className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold">
              {group.label}
            </p>
            {group.note && <p className="mt-0.5 text-[11px] text-ink-muted">{group.note}</p>}
          </div>
          {group.roles.map((role) => {
            const drift = driftByRole[role] ?? 0;
            const active = role === selected;
            return (
              <button
                key={role}
                type="button"
                onClick={() => onSelect(role)}
                className={[
                  'w-full text-left px-3 py-2 text-sm border-b border-surface-border/60 flex items-center justify-between gap-2',
                  active ? 'bg-navy-50 text-ink font-medium' : 'hover:bg-surface-muted/40 text-ink',
                ].join(' ')}
              >
                <span className="truncate">{humanRole(role)}</span>
                {drift > 0 && (
                  <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
                    {drift}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function RoleEditor({
  role,
  matrix,
  onLevel,
  onToggleOp,
  onBulkLevel,
}: {
  role: UserRole;
  matrix: RbacMatrix;
  onLevel: (r: UserRole, k: PageKey, l: AccessLevel) => void;
  onToggleOp: (r: UserRole, k: PageKey, op: CrudOp) => void;
  onBulkLevel: (r: UserRole, l: AccessLevel) => void;
}) {
  const unenforced = UNENFORCED_ROLES.has(role);

  return (
    <div className="space-y-5 min-w-0">
      <div className="rounded border border-surface-border bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{humanRole(role)}</h2>
          <p className="text-xs text-ink-muted mt-0.5">
            <code className="font-mono">{role}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-muted">Set all modules to</span>
          {LEVEL_ORDER.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => onBulkLevel(role, l)}
              title={LEVEL_HINTS[l]}
              className={`px-2 py-1 rounded border text-xs font-semibold ${LEVEL_CELL[l]}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {unenforced && (
        <p className="text-xs text-ink-muted rounded border border-surface-border bg-surface-muted/40 px-3 py-2">
          This is a reserved role name. No guard enforces it today, so configuring it has no
          effect until the role is wired up.
        </p>
      )}

      {RBAC_MODULE_GROUPS.map((group) => (
        <section key={group.label} className="rounded border border-surface-border bg-white overflow-hidden">
          <header className="px-4 py-2 bg-surface-muted/40 border-b border-surface-border">
            <h3 className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold">
              {group.label}
            </h3>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-surface-border">
                <tr>
                  <th className="px-4 py-2 min-w-[200px]">Module</th>
                  <th className="px-4 py-2 w-[180px]">Level</th>
                  {CRUD_OPS.map((op) => (
                    <th key={op} className="px-2 py-2 text-center w-[64px]">
                      {op.charAt(0).toUpperCase()}
                      <span className="hidden xl:inline">{op.slice(1)}</span>
                    </th>
                  ))}
                  <th className="px-4 py-2 w-[110px]">Live now</th>
                </tr>
              </thead>
              <tbody>
                {group.modules.map((m) => {
                  const perms = permsFor(matrix, role, m.key);
                  const level = levelOf(perms);
                  const live = liveLevelFor(role, m.key);
                  const drift = isDrifted(matrix, role, m.key);
                  return (
                    <tr key={m.key} className="border-b border-surface-border/60 last:border-0">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-ink">{m.label}</div>
                        <div className="text-[11px] text-ink-muted font-mono">{m.key}</div>
                        {m.note && <div className="text-[11px] text-ink-muted mt-0.5">{m.note}</div>}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          {LEVEL_ORDER.map((l) => (
                            <button
                              key={l}
                              type="button"
                              title={`${LEVEL_LABELS[l]} — ${LEVEL_HINTS[l]}`}
                              onClick={() => onLevel(role, m.key, l)}
                              className={[
                                'w-7 h-7 rounded border text-xs font-semibold transition-colors',
                                level === l
                                  ? LEVEL_CELL[l]
                                  : 'border-surface-border text-ink-muted hover:bg-surface-muted/60',
                              ].join(' ')}
                            >
                              {l}
                            </button>
                          ))}
                          {level === 'CUSTOM' && (
                            <span
                              title={LEVEL_HINTS.CUSTOM}
                              className={`ml-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${LEVEL_CELL.CUSTOM}`}
                            >
                              Custom
                            </span>
                          )}
                        </div>
                      </td>
                      {CRUD_OPS.map((op) => (
                        <td key={op} className="px-2 py-2.5 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-surface-border accent-navy-600"
                            checked={perms[op]}
                            onChange={() => onToggleOp(role, m.key, op)}
                            aria-label={`${op} on ${m.label} for ${humanRole(role)}`}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-2.5">
                        <span
                          className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${LEVEL_CELL[live]}`}
                          title={
                            live === 'N'
                              ? 'The app blocks this role here today.'
                              : 'The app allows this role here today.'
                          }
                        >
                          {live === 'N' ? 'Blocked' : 'Allowed'}
                        </span>
                        {drift && (
                          <span className="ml-1.5 text-[10px] text-violet-700 font-semibold">
                            changed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

function MatrixOverview({
  matrix,
  onJumpToRole,
}: {
  matrix: RbacMatrix;
  onJumpToRole: (r: UserRole) => void;
}) {
  return (
    <div className="rounded border border-surface-border bg-white overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <table className="text-sm border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-20 bg-surface-muted px-3 py-2 text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-r border-surface-border min-w-[200px]">
                Role \ Module
              </th>
              {RBAC_MODULE_GROUPS.map((group) =>
                group.modules.map((m) => (
                  <th
                    key={m.key}
                    title={`${group.label} — ${m.label}`}
                    className="sticky top-0 z-10 bg-surface-muted px-1 py-2 border-b border-surface-border text-[10px] font-medium text-ink-muted whitespace-nowrap"
                  >
                    <div className="h-28 flex items-end justify-center">
                      <span className="[writing-mode:vertical-rl] rotate-180">{m.label}</span>
                    </div>
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {RBAC_ROLE_GROUPS.map((group) => (
              <Fragment key={group.label}>
                <tr className="bg-surface-muted/30">
                  <td
                    colSpan={ALL_MODULES.length + 1}
                    className="sticky left-0 px-3 py-1 text-[10px] uppercase tracking-wider text-ink-muted font-semibold border-b border-surface-border"
                  >
                    {group.label}
                  </td>
                </tr>
                {group.roles.map((role) => (
                  <tr key={role} className="hover:bg-surface-muted/30">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-white px-3 py-1.5 text-left font-medium text-ink border-b border-r border-surface-border whitespace-nowrap"
                    >
                      <button
                        type="button"
                        className="hover:underline text-left"
                        onClick={() => onJumpToRole(role)}
                      >
                        {humanRole(role)}
                      </button>
                    </th>
                    {ALL_MODULES.map((m) => {
                      const level = levelOf(permsFor(matrix, role, m.key));
                      return (
                        <td
                          key={m.key}
                          className="px-1 py-1.5 text-center border-b border-surface-border/60"
                          title={`${humanRole(role)} — ${m.label}: ${LEVEL_LABELS[level]}`}
                        >
                          <span
                            className={`inline-block w-6 h-6 leading-6 rounded border text-[11px] font-semibold ${LEVEL_CELL[level]}`}
                          >
                            {LEVEL_SHORT[level]}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="rounded border border-surface-border bg-surface-muted/30 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-3.5 w-3.5 text-ink-muted" />
        <p className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold">
          Access levels
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2">
        {(['N', 'V', 'C', 'A', 'CUSTOM'] as DisplayLevel[]).map((l) => (
          <div key={l} className="flex items-start gap-2">
            <span
              className={`shrink-0 inline-block w-6 h-6 leading-6 text-center rounded border text-[11px] font-semibold ${LEVEL_CELL[l]}`}
            >
              {LEVEL_SHORT[l]}
            </span>
            <div className="text-xs">
              <div className="font-medium text-ink">{LEVEL_LABELS[l]}</div>
              <div className="text-ink-muted">{LEVEL_HINTS[l]}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-ink-muted">
        Read is the base of the other three — ticking Create, Update or Delete turns Read on
        automatically, because a role cannot act on what it cannot see.
      </p>
    </div>
  );
}
